// Prévia de link — a regra do que é link e a normalização (spec de 27/10/2026, §3.1 e §4.3).
//
// A bolha do chat, o feed e o atendimento decidem aqui, todos pela mesma regra, o que é link, qual é o
// primeiro e qual é o endereço que se manda ao servidor e se compara depois.
//
// 🔴 `normalizarLink` é a `normalizar_link` do servidor (`services/previa_de_link/endereco.py`), com a mesma
// saída para o mesmo texto: o arquivo `__tests__/normalizacao.casos.json` roda nos dois lados. Por isso ela
// NÃO usa o `href` do `URL` do navegador (que reescreve caminho e consulta, aceita `https:/x.com`, trata `\` como
// `/`, decodifica `%61` no host e aceita rótulo vazio): o servidor recusa tudo isso, e UM link recusado dá 422 ao
// lote inteiro, apagando todos os cartões da tela. Na dúvida ela recusa (`null`): o link segue clicável, só fica
// sem cartão. O `URL` entra só onde não há como evitar: conferir o IPv6. Caractere fora do ASCII na autoridade
// (usuário, senha ou host) é sempre recusado: o servidor confere o host por uma ida e volta pelo IDNA de 2003
// (ß vira ss, o invisível some, o ponto ideográfico vira ponto) e o navegador usa outra tabela; espelhar as duas
// não dá, e errar para o lado de aceitar dá 422 ao lote. O link segue clicável, só sem cartão.

/**
 * A fonte da regex do link: a bolha do chat a compõe com as outras alternativas (menção, hashtag, emoji).
 * Use com as flags `gi`. `www.` só vale solto: não acha dentro de `awww.x.com`, `x.www.y.com`, `a@www.b.com`.
 */
export const FONTE_DO_LINK = String.raw`https?:\/\/[^\s<>"']+|(?<![\w@./-])www\.[^\s<>"']+`;

export interface LinkDoTexto {
  /** Onde o link começa no texto. */
  inicio: number;
  /** Onde o link termina (exclusivo): `texto.slice(inicio, fim)` é o `texto` do link. */
  fim: number;
  /** Como foi digitado, sem a pontuação do fim da frase. */
  texto: string;
  /** Onde o clique vai: o `texto`, e `www.` ganha `https://`. */
  href: string;
}

export interface PrimeiroLink {
  texto: string;
  href: string;
  /** O endereço que o servidor guarda e compara; `null` = recusado (p.ex. mais de 2048 caracteres). */
  normalizado: string | null;
  /**
   * O link é do host do próprio SE_WEB (mesmo host e mesma porta do navegador), seja tela ou não: nunca vai ao
   * servidor. Quem decide o router e o cartão da casa pergunta também se é TELA do app (`ehTelaDoLink`,
   * `previaDoCartao.ts`): o arquivo assinado `/api/...`, `/health` e os estáticos são da casa e não são tela.
   */
  casa: boolean;
  /** Tem usuário e senha: segue clicável, mas sem cartão e sem busca. */
  comCredencial: boolean;
  /** Há algo depois do link no texto (o espaço ou o Enter após a URL): a pessoa seguiu em frente. */
  terminado: boolean;
}

const TAMANHO_MAXIMO_DO_LINK = 2048;
const TAMANHO_MAXIMO_DO_HOST = 253;
const TAMANHO_MAXIMO_DO_ROTULO = 63;
const PORTA_MAXIMA = 65535;
const PORTA_PADRAO: Record<string, number> = { http: 80, https: 443 };

// --- o que é link no texto -------------------------------------------------------------------------------

/** Pontuação que fecha a frase e não pertence ao endereço. */
const PONTUACAO_FINAL = /[.,;:!?"']$/;
const LINK_DE_PONTA_A_PONTA = new RegExp(`^(?:${FONTE_DO_LINK})$`, "i");

/**
 * O trecho todo, de ponta a ponta, é um link. Quem compõe `FONTE_DO_LINK` com outras alternativas (a bolha do chat)
 * confere o trecho aparado por aqui: aparar `http://.` deixa `http://`, que não é link.
 */
export function linkCompleto(aparado: string): boolean {
  return LINK_DE_PONTA_A_PONTA.test(aparado);
}

function contar(texto: string, caractere: string): number {
  return texto.split(caractere).length - 1;
}

/**
 * Tira do fim do link o que é da frase: `. , ; : ! ? " '`, e o `)` que não fecha nenhum `(` do próprio link
 * (`(veja https://x.com/a)` perde o `)`; `.../Foo_(bar)` mantém o seu). Repete até estabilizar: `Foo_(bar)).`.
 */
export function apararLink(bruto: string): string {
  let link = bruto;
  for (;;) {
    const antes = link;
    link = link.replace(PONTUACAO_FINAL, "");
    if (link.endsWith(")") && contar(link, ")") > contar(link, "(")) link = link.slice(0, -1);
    if (link === antes) return link;
  }
}

/**
 * `www.` solto não é um endereço: ganha o esquema (só no href; o `texto` fica como foi digitado). Exportada para quem
 * compõe `FONTE_DO_LINK` com outras alternativas (a bolha do chat) montar o `href` pela MESMA regra de `acharLinks`.
 */
export function comEsquema(link: string): string {
  return /^www\./i.test(link) ? `https://${link}` : link;
}

function* varrerLinks(texto: string): Generator<LinkDoTexto> {
  for (const achado of texto.matchAll(new RegExp(FONTE_DO_LINK, "gi"))) {
    const aparado = apararLink(achado[0]);
    if (!linkCompleto(aparado)) continue;
    const inicio = achado.index ?? 0;
    yield { inicio, fim: inicio + aparado.length, texto: aparado, href: comEsquema(aparado) };
  }
}

/** Todos os links do texto, na ordem em que aparecem. */
export function acharLinks(texto: string): LinkDoTexto[] {
  return Array.from(varrerLinks(texto));
}

// --- a normalização (espelho do servidor) ----------------------------------------------------------------

/** Os brancos do Python (`str.isspace`), em faixas de code point: o `strip()` do servidor tira os das pontas. */
const BRANCOS_DO_PYTHON: ReadonlyArray<readonly [number, number]> = [
  [0x09, 0x0d],
  [0x1c, 0x20],
  [0x85, 0x85],
  [0xa0, 0xa0],
  [0x1680, 0x1680],
  [0x2000, 0x200a],
  [0x2028, 0x2029],
  [0x202f, 0x202f],
  [0x205f, 0x205f],
  [0x3000, 0x3000],
];

/** `esquema://autoridade/caminho?consulta#fragmento`: só http e https, e a autoridade vai até o 1º `/`, `?` ou `#`. */
const ENDERECO = /^(https?):\/\/([^/?#]*)([^?#]*)(?:\?([^#]*))?/i;
/** Host de verdade: letras, números, hífen, sublinhado e ponto. */
const NOME_DE_HOST = /^[a-z0-9_.-]+$/;
const COM_COLCHETE = /[[\]]/;
/** Fora do ASCII imprimível (o branco e o controle já foram recusados antes, então só sobra o não ASCII). */
const NAO_ASCII = /[^\x20-\x7e]/;

/**
 * Surrogate solto: uma unidade alta (D800 a DBFF) sem a baixa logo depois, ou uma baixa (DC00 a DFFF) sem a alta
 * logo antes. O par bem formado (um emoji) é UM caractere e passa. Sem a flag `u`, a regex lê unidades UTF-16.
 * `String.prototype.isWellFormed` seria o mesmo teste, mas é ES2024 e o `lib` do `tsconfig.app.json` é ES2020.
 */
const SURROGATE_SOLTO = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/;

interface Partes {
  esquema: string;
  autoridade: string;
  caminho: string;
  consulta: string;
}

interface Autoridade {
  /** O que vem antes do último `@`, ou `null` se não há `@`. */
  usuario: string | null;
  /** Como foi digitado (sem os colchetes do IPv6). */
  host: string;
  colchetes: boolean;
  /** O texto depois do `:`; `null` quando ausente ou vazio. */
  porta: string | null;
}

function ehBranco(codigo: number): boolean {
  return BRANCOS_DO_PYTHON.some(([de, ate]) => codigo >= de && codigo <= ate);
}

function tirarBrancoDasPontas(texto: string): string {
  let inicio = 0;
  let fim = texto.length;
  while (inicio < fim && ehBranco(texto.charCodeAt(inicio))) inicio++;
  while (fim > inicio && ehBranco(texto.charCodeAt(fim - 1))) fim--;
  return texto.slice(inicio, fim);
}

/** Espaço, controle (inclusive o NUL) ou qualquer branco unicode no meio do link. */
function temBrancoOuControle(texto: string): boolean {
  for (let i = 0; i < texto.length; i++) {
    const codigo = texto.charCodeAt(i);
    if (codigo <= 0x20 || codigo === 0x7f || ehBranco(codigo)) return true;
  }
  return false;
}

/** `true` se o texto passa de `maximo` CARACTERES (o `len` do Python; o `length` do JS conta unidades UTF-16). */
function passaDoTamanho(texto: string, maximo: number): boolean {
  if (texto.length <= maximo) return false;
  if (texto.length > maximo * 2) return true;
  return Array.from(texto).length > maximo;
}

function lerComoUrl(endereco: string): URL | null {
  try {
    return new URL(endereco);
  } catch {
    return null;
  }
}

/** Parte o endereço (sem conferir nada); `null` se não começa por `http://` ou `https://`. */
function separar(link: string): Partes | null {
  const achado = ENDERECO.exec(comEsquema(link));
  if (!achado) return null;
  return {
    esquema: achado[1].toLowerCase(),
    autoridade: achado[2],
    caminho: achado[3],
    consulta: achado[4] ?? "",
  };
}

function lerAutoridade(autoridade: string): Autoridade | null {
  const arroba = autoridade.lastIndexOf("@");
  const usuario = arroba < 0 ? null : autoridade.slice(0, arroba);
  const informacao = autoridade.slice(arroba + 1);
  if (usuario !== null && COM_COLCHETE.test(usuario)) return null;

  if (informacao.startsWith("[")) {
    const fecha = informacao.indexOf("]");
    if (fecha < 0) return null;
    const resto = informacao.slice(fecha + 1);
    if (resto !== "" && !resto.startsWith(":")) return null;
    return {
      usuario,
      host: informacao.slice(1, fecha),
      colchetes: true,
      porta: resto.slice(1) || null,
    };
  }
  if (COM_COLCHETE.test(informacao)) return null;
  const doisPontos = informacao.indexOf(":");
  if (doisPontos < 0) return { usuario, host: informacao, colchetes: false, porta: null };
  return {
    usuario,
    host: informacao.slice(0, doisPontos),
    colchetes: false,
    porta: informacao.slice(doisPontos + 1) || null,
  };
}

/** Nome em ASCII e minúsculas: o ponto final cai uma vez; cada rótulo tem de 1 a 63 (o último pode ser vazio). */
function nomeValido(nome: string): boolean {
  const semPontoFinal = nome.endsWith(".") ? nome.slice(0, -1) : nome;
  if (!NOME_DE_HOST.test(semPontoFinal)) return false;
  const rotulos = semPontoFinal.split(".");
  const ultimo = rotulos.pop() ?? "";
  return (
    ultimo.length <= TAMANHO_MAXIMO_DO_ROTULO &&
    rotulos.every((r) => r.length >= 1 && r.length <= TAMANHO_MAXIMO_DO_ROTULO)
  );
}

/** O host como vai no endereço guardado (minúsculas, como foi digitado, IPv6 com colchetes), ou `null`. Já em ASCII. */
function hostDoEndereco(digitado: string, colchetes: boolean): string | null {
  const host = digitado.toLowerCase();
  if (colchetes) {
    // IPv6: o navegador tem de lê-lo; sai como foi digitado (o servidor também não o reescreve).
    return host.includes(":") && lerComoUrl(`http://[${host}]/`) ? `[${host}]` : null;
  }
  if (host === "" || passaDoTamanho(host, TAMANHO_MAXIMO_DO_HOST)) return null;
  return nomeValido(host) ? host : null;
}

/**
 * O link na forma única pela qual o servidor o guarda e o compara (a mesma `normalizar_link` dele): sem branco
 * nas pontas e sem `#fragmento`; esquema e host em minúsculas; porta padrão (80 no http, 443 no https) some;
 * caminho vazio vira `/`; `www.` vira `https://www.`; consulta vazia (`?`) some. Usuário e senha FICAM (quem os
 * recusa é o `conferir_alvo`, no servidor). O tamanho de 2048 vale para a entrada e para a saída. Caractere fora do
 * ASCII no usuário, na senha ou no host recusa o link (no caminho, na consulta e no fragmento passa), e surrogate
 * solto em qualquer parte também. `null` = recusado.
 */
export function normalizarLink(link: string): string | null {
  const limpo = tirarBrancoDasPontas(link);
  if (passaDoTamanho(limpo, TAMANHO_MAXIMO_DO_LINK) || temBrancoOuControle(limpo)) return null;
  // Surrogate solto não vira UTF-8: o servidor o recusa (sem isto ele levantava `UnicodeEncodeError` no hash e o
  // lote inteiro dava 500). Em qualquer parte do link, inclusive no `#fragmento` que a saída tira.
  if (SURROGATE_SOLTO.test(limpo)) return null;
  const partes = separar(limpo);
  if (!partes || NAO_ASCII.test(partes.autoridade)) return null;
  const autoridade = lerAutoridade(partes.autoridade);
  if (!autoridade) return null;
  const host = hostDoEndereco(autoridade.host, autoridade.colchetes);
  if (host === null) return null;

  let porta: number | null = null;
  if (autoridade.porta !== null) {
    if (!/^[0-9]+$/.test(autoridade.porta)) return null;
    porta = Number(autoridade.porta);
    if (porta > PORTA_MAXIMA) return null;
  }

  const usuario = autoridade.usuario === null ? "" : `${autoridade.usuario}@`;
  const comPorta = porta === null || porta === PORTA_PADRAO[partes.esquema] ? "" : `:${porta}`;
  const consulta = partes.consulta ? `?${partes.consulta}` : "";
  const normalizado = `${partes.esquema}://${usuario}${host}${comPorta}${partes.caminho || "/"}${consulta}`;
  return passaDoTamanho(normalizado, TAMANHO_MAXIMO_DO_LINK) ? null : normalizado;
}

// --- o que se faz com o link -----------------------------------------------------------------------------

/**
 * O link é do próprio SE_WEB: mesmo `host` do navegador (com a porta, se não for a padrão). Lê como o navegador
 * lê, que é como o clique vai se comportar. `hostDaCasa` padrão: `window.location.host`.
 */
export function ehDaCasa(href: string, hostDaCasa: string = window.location.host): boolean {
  const url = lerComoUrl(comEsquema(href.trim()));
  if (!url || (url.protocol !== "http:" && url.protocol !== "https:")) return false;
  return url.host === hostDaCasa.trim().toLowerCase();
}

/** O domínio do link, em minúsculas, com o `www.` (é o que o cartão mostra), ou `""` se não se lê. */
export function dominioDe(href: string): string {
  const partes = separar(href.trim());
  const autoridade = partes && lerAutoridade(partes.autoridade);
  return autoridade ? autoridade.host.toLowerCase() : "";
}

/** Os dois são o mesmo link depois de normalizar; faltando um lado, ou sendo recusado, nunca são. */
export function mesmoLink(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  const normalizadoA = normalizarLink(a);
  return normalizadoA !== null && normalizadoA === normalizarLink(b);
}

/** O primeiro link do texto, com o que as telas precisam saber dele; `null` se não há. */
export function primeiroLink(
  texto: string,
  hostDaCasa: string = window.location.host
): PrimeiroLink | null {
  const primeiro = varrerLinks(texto).next();
  if (primeiro.done) return null;
  const { texto: digitado, href, fim } = primeiro.value;
  const partes = separar(href);
  return {
    texto: digitado,
    href,
    normalizado: normalizarLink(href),
    casa: ehDaCasa(href, hostDaCasa),
    comCredencial: partes !== null && partes.autoridade.includes("@"),
    terminado: fim < texto.length,
  };
}

/**
 * O `link_dispensado` que vai no envio (o ✕ que tirou o cartão): o normalizado do primeiro link, e só quando o ✕
 * foi dado NESSE link (`semCartaoDe`, em qualquer forma) ou quando `forcar` (o texto que sai à parte, com anexo).
 * O servidor só aceita `https?://\S+` e guarda o que recebe, então qualquer outra forma dá 422 no ENVIO.
 * Devolve `undefined`, nunca `null`: chave ausente é "sem ✕" (no atendimento `null` dá 422; no feed `null` limpa).
 */
export function linkDispensadoParaEnviar(
  primeiro: PrimeiroLink | null,
  semCartaoDe: string | null,
  opcoes?: { forcar?: boolean }
): string | undefined {
  if (!primeiro || primeiro.comCredencial || primeiro.normalizado === null) return undefined;
  if (opcoes?.forcar || mesmoLink(primeiro.normalizado, semCartaoDe)) return primeiro.normalizado;
  return undefined;
}
